import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './Category.scss';
import agent from '../../../agent';

const CreateCategory = ({ onClose, currentUser, onSuccess, editCategory = null }) => {
    
    const isEditMode = !!editCategory;
    const [name, setName] = useState('');
    const [signature, setSignature] = useState('');
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loadingCategory, setLoadingCategory] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const quillRef = useRef(null);
    const quillModules = useMemo(() => ({
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ color: [] }, { background: [] }],
            [{ size: ['small', false, 'large', 'huge'] }],
            [{ align: [] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link', 'image'],
            ['clean'],
        ],
    }), []);

    const quillFormats = [
        'bold', 'italic', 'underline', 'strike',
        'color', 'background',
        'size', 'align',
        'list', 'bullet',
        'link', 'image',
    ];

    const loadCategoryData = async (categoryId) => {
        if (!categoryId) return;
        setLoadingCategory(true);
        setApiError(null);
        try {
            const response = await agent.categories.getCategoryById(categoryId);
            if (response && response.isSuccess && response.data && response.data.category) {
                const cat = response.data.category;
                setName(cat.name || '');
                setSignature(cat.signature || '');
            } else {
                setApiError('Failed to load category data.');
            }
        } catch (err) {
            console.error('Error loading category:', err);
            setApiError('Failed to load category data. Please try again.');
        } finally {
            setLoadingCategory(false);
        }
    };

    // Reset / load when editCategory changes
    useEffect(() => {
        if (editCategory && editCategory.id) {
            loadCategoryData(editCategory.id);
        } else {
            setName('');
            setSignature('');
            setErrors({});
            setApiError(null);
        }
    }, [editCategory]);

    // Auto-dismiss success after 4 s
    useEffect(() => {
        if (!successMessage) return;
            const t = setTimeout(() => setSuccessMessage(null), 4000);
            return () => clearTimeout(t);
    }, [successMessage]);

    // ── Handlers ──────────────────────────────────────────────────
    const handleSignatureChange = useCallback((value) => {
        setSignature(value);
    }, []);

    const validate = () => {
        const errs = {};
        if (!name.trim()) errs.name = 'Category name is required.';
        return errs;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length) {
            setErrors(errs);
            return;
        }

        setIsSubmitting(true);
        setApiError(null);

        try {
            const payload = { name: name.trim(), signature };
            let response;
            if (isEditMode && editCategory.id) {
                response = await agent.categories.updateCategory(editCategory.id, payload);
            } else {
                response = await agent.categories.createCategory(payload);
            }

            if (response && response.isSuccess) {
                if (onSuccess) onSuccess();
                onClose();
            } else {
                setApiError(response?.message || (isEditMode ? 'Failed to update category.' : 'Failed to create category.'));
            }
        } catch (err) {
            console.error('Error saving category:', err);
            setApiError(err?.response?.body?.message || err?.message || 'An unexpected error occurred.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Loading / error screens ────────────────────────────────────
    if (loadingCategory) {
        return (
            <div className="create-category">
                <div className="create-category-header d-flex justify-content-between align-items-center px-4 py-2 border-bottom bg-light sticky-top me-0 flex-wrap">
                    <h2>Edit Category</h2>
                    <button className="create-category-close" onClick={onClose}>✕</button>
                </div>
                <div className="create-category-loading-container">
                    <div className="spinner" />
                        <span>Loading category data…</span>
                    </div>
                </div>
        );
    }

    if (isEditMode && apiError && !name) {
        return (
            <div className="create-category">
                <div className="create-category-header d-flex justify-content-between align-items-center px-4 py-2 border-bottom bg-light sticky-top me-0 flex-wrap">
                    <h2>Edit Category</h2>
                    <button className="create-category-close" onClick={onClose}>✕</button>
                </div>
                <div className="create-category-error-container">
                    <span className="error-icon">⚠️</span>
                    <h3>Failed to Load Category</h3>
                    <p>{apiError}</p>
                    <div className="create-category-error-actions">
                        <button type="button" className="create-category-retry-btn"
                            onClick={() => loadCategoryData(editCategory.id)}>
                        Retry </button>
                        <button type="button" className="create-category-cancel" onClick={onClose}>
                        Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── Main render ────────────────────────────────────────────────
    return (
        <div className="create-category">
            {/* Header */}
            <div className="create-category-header d-flex justify-content-between align-items-center px-4 py-2 border-bottom bg-light sticky-top me-0 flex-wrap">
                <h2>{isEditMode ? `Edit Category` : 'Create Category'}</h2>
                <button className="create-category-close" onClick={onClose}>✕</button>
            </div>

            <form onSubmit={handleSubmit} className="create-category-form p-4">
                {/* Alerts */}
                {successMessage && (
                <div className="create-category-success-message">
                    <span>✅</span> {successMessage}
                </div>
                )}
                {apiError && (
                <div className="create-category-error-message">
                    <span>⚠️</span> {apiError}
                </div>
                )}
    
                {/* Name field */}
                <div className="create-category-section">
                    <h3 className="create-category-section-title">Category Details</h3>
    
                    <div className="create-category-field">
                    <label htmlFor="category-name">Category Name <span className="required">*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            if (errors.name) setErrors((prev) => ({ ...prev, name: '' }));
                            }}
                        placeholder="e.g. Waterproofing"
                        className={errors.name ? 'error' : ''}
                    />
                    {errors.name && <span className="error-message">{errors.name}</span>}
                </div>
            </div>
    
            {/* Signature editor */}
            <div className="create-category-section">
                <h3 className="create-category-section-title">Email Signature</h3>
                <p className="create-category-section-hint">
                This signature will be appended to emails sent for this category.
                </p>
    
                <div className="create-category-field">
                    <div className="quill-editor-container">
                        <ReactQuill
                            ref={quillRef}
                            theme="snow"
                            value={signature}
                            onChange={handleSignatureChange}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Add your email signature here…"
                        />
                    </div>
                </div>
            </div>
    
            {/* Actions */}
            <div className="create-category-actions">
                <button
                    type="button"
                    className="create-category-cancel"
                    onClick={onClose}
                    disabled={isSubmitting}
                >
                Cancel
                </button>
                <button
                    type="submit"
                    className="create-category-save"
                    disabled={isSubmitting}
                >
                {isSubmitting
                  ? isEditMode ? 'Updating…' : 'Creating…'
                  : isEditMode ? 'Update Category' : 'Create Category'}
                </button>
            </div>
        </form>
    </div>
    );
};

export default CreateCategory;